import { useEffect, useCallback, useRef } from 'react'
import { useKemCardStore, KemCardScreenState } from '@/store/useKemCardStore'
import { useGetWallet } from './useGetWallet'
import { useVerifyCardIssueStatus } from './useVerifyCardIssueStatus'
import { mapAxiosErrorToApiError } from '@/utils'
import { ApiErrorCode } from '@/api/apiTypes'

const POLLING_INTERVAL_MS = 15 * 1000 // 15 seconds

/**
 * Hook to determine and update the KemCard screen state
 * This centralizes all the logic for determining which card screen to display
 *
 * Usage:
 * - Call this in DashboardContent for initial data load
 * - Call this in KemCardScreen to ensure fresh state on mount
 *
 * The hook automatically:
 * 1. Fetches wallet data to check for existing cards
 * 2. If no card exists, checks the card issue status
 * 3. Determines the appropriate screen to show based on the data
 * 4. Updates the store with the determined state
 *
 * Polling behavior:
 * - When enablePolling is true and user doesn't have a card, polls every 15 seconds
 * - Polling stops when user has a card (WITH_CARD state)
 */
export const useKemCardScreenState = (options?: { enablePolling?: boolean }) => {
  const { enablePolling = false } = options || {}
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const setScreenState = useKemCardStore((state) => state.setScreenState)

  // Get wallet data to check for existing cards
  const {
    data: walletData,
    isLoading: isWalletLoading,
    error: walletError,
    refetch: refetchWallet,
  } = useGetWallet()

  // Get active card id associated with the user from wallet API
  const kemCardId = walletData?.cards?.[0]?.id
  const hasCard = walletData?.cards && walletData.cards.length > 0
  const cardData = walletData?.cards?.[0]

  // Verify if card issued if user has verified their identity and there's no active card
  const {
    data: issueData,
    isLoading: isLoadingIssueState,
    error: issueError,
    refetch: refetchIssueStatus,
  } = useVerifyCardIssueStatus({
    enabled: !isWalletLoading && !hasCard, // Only check if wallet data is loaded and we don't have a card
  })
  const ISSUE_STATE = issueData?.card_issue_state
  const INTERLACE_ISSUE_STATE = issueData?.interlace_card_issue_state
  const RAIN_ISSUE_STATE = issueData?.rain_card_issue_state
  const { code: ISSUE_ERROR_CODE } = issueError
    ? mapAxiosErrorToApiError(issueError)
    : { code: '' }

  const USER_NOT_VERIFIED = ISSUE_ERROR_CODE === 'USER_NOT_VERIFIED'
  const PHONE_NUMBER_NOT_SET = ISSUE_ERROR_CODE === 'PHONE_NUMBER_NOT_SET'
  const EMAIL_NOT_CONFIRMED = ISSUE_ERROR_CODE === 'EMAIL_NOT_CONFIRMED'

  // Determine the screen state based on all the data
  const determineScreenState = useCallback((): KemCardScreenState => {
    // Handle loading states
    if (isWalletLoading) {
      return {
        screenType: 'LOADING',
        isLoading: true,
      }
    }

    // Handle wallet error
    if (walletError) {
      return {
        screenType: 'ERROR',
        errorCode: mapAxiosErrorToApiError(walletError).code,
        isLoading: false,
      }
    }

    // If we have a card in wallet, show the card interface
    if (hasCard && kemCardId) {
      return {
        screenType: 'WITH_CARD',
        kemCardId,
        cardData,
        isLoading: false,
      }
    }

    // If there's an unexpected error on issue status endpoint
    if (
      ISSUE_ERROR_CODE &&
      !USER_NOT_VERIFIED &&
      !PHONE_NUMBER_NOT_SET &&
      !EMAIL_NOT_CONFIRMED
    ) {
      return {
        screenType: 'ERROR',
        errorCode: ISSUE_ERROR_CODE as ApiErrorCode,
        isLoading: false,
      }
    }

    // Show Initial card screen if:
    // - Still loading issue state
    // - User not verified (needs to complete KYC)
    // - Phone number not set
    // - Email not confirmed
    if (
      isLoadingIssueState ||
      USER_NOT_VERIFIED ||
      PHONE_NUMBER_NOT_SET ||
      EMAIL_NOT_CONFIRMED
    ) {
      return {
        screenType: 'WITHOUT_CARD',
        errorCode: ISSUE_ERROR_CODE as ApiErrorCode,
        isLoading: isLoadingIssueState,
      }
    }

    // Show Processing card if card is in validation or approved state
    if (
      ISSUE_STATE === 'VALIDATION' ||
      ISSUE_STATE === 'APPROVED' ||
      INTERLACE_ISSUE_STATE === 'VALIDATION' ||
      INTERLACE_ISSUE_STATE === 'APPROVED' ||
      RAIN_ISSUE_STATE === 'VALIDATION' ||
      RAIN_ISSUE_STATE === 'APPROVED'
    ) {
      return {
        screenType: 'PROCESSING',
        issueState: ISSUE_STATE,
        isLoading: false,
      }
    }

    // Show Rejected card if application was rejected
    if (
      ISSUE_STATE === 'REJECTED' ||
      RAIN_ISSUE_STATE === 'REJECTED' ||
      INTERLACE_ISSUE_STATE === 'REJECTED'
    ) {
      return {
        screenType: 'REJECTED',
        issueState: ISSUE_STATE,
        isLoading: false,
      }
    }

    // Card is issued and we have card ID - show activated card
    if (
      (ISSUE_STATE === 'ISSUED' ||
        RAIN_ISSUE_STATE === 'ISSUED' ||
        INTERLACE_ISSUE_STATE === 'ISSUED') &&
      kemCardId
    ) {
      return {
        screenType: 'WITH_CARD',
        kemCardId,
        cardData,
        issueState: ISSUE_STATE,
        isLoading: false,
      }
    }

    // If card is issued but we don't have card ID yet, show processing
    // This can happen when card is just issued but wallet data hasn't been updated
    if (
      (ISSUE_STATE === 'ISSUED' ||
        RAIN_ISSUE_STATE === 'ISSUED' ||
        INTERLACE_ISSUE_STATE === 'ISSUED') &&
      !kemCardId
    ) {
      return {
        screenType: 'PROCESSING',
        issueState: ISSUE_STATE,
        isLoading: false,
      }
    }

    // Default fallback - show without card screen
    return {
      screenType: 'WITHOUT_CARD',
      isLoading: false,
    }
  }, [
    isWalletLoading,
    isLoadingIssueState,
    hasCard,
    kemCardId,
    cardData,
    ISSUE_STATE,
    ISSUE_ERROR_CODE,
    USER_NOT_VERIFIED,
    PHONE_NUMBER_NOT_SET,
    EMAIL_NOT_CONFIRMED,
    walletError,
    issueError,
  ])

  // Get current persisted state to avoid overwriting valid cached state with loading state
  const currentScreenState = useKemCardStore((state) => state.screenState)

  // Update the store whenever the determined state changes
  // But DON'T overwrite a valid cached state (WITH_CARD, WITHOUT_CARD, etc.) with LOADING
  // This prevents flash when reloading the page - we show cached state while data loads
  useEffect(() => {
    const newState = determineScreenState()

    // If we're in a loading state but already have a meaningful cached state,
    // keep the cached state to prevent UI flash
    const hasMeaningfulCachedState =
      currentScreenState.screenType !== 'LOADING' &&
      currentScreenState.screenType !== 'ERROR'

    if (newState.screenType === 'LOADING' && hasMeaningfulCachedState) {
      return
    }

    // If we're in PROCESSING state (set optimistically after card creation),
    // don't overwrite it with WITHOUT_CARD until the backend confirms a different state.
    // This preserves the PROCESSING screen after submitting card creation.
    if (
      currentScreenState.screenType === 'PROCESSING' &&
      newState.screenType === 'WITHOUT_CARD'
    ) {
      return
    }

    setScreenState(newState)
  }, [determineScreenState, setScreenState, currentScreenState.screenType])

  // Check if user already has a card from the persisted state
  const userHasCard = currentScreenState.screenType === 'WITH_CARD'

  // Provide a manual refresh function that refetches both queries
  // Only refetch if user doesn't have a card yet
  const refresh = useCallback(async () => {
    if (userHasCard) {
      return
    }
    await Promise.all([refetchWallet(), refetchIssueStatus()])
  }, [refetchWallet, refetchIssueStatus, userHasCard])

  // Setup polling when enabled and user doesn't have a card
  useEffect(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    // Only poll if enabled and user doesn't have a card
    if (enablePolling && !userHasCard) {
      pollingIntervalRef.current = setInterval(() => {
        refetchWallet()
        refetchIssueStatus()
      }, POLLING_INTERVAL_MS)
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [enablePolling, userHasCard, refetchWallet, refetchIssueStatus])

  return {
    screenState: determineScreenState(),
    refresh,
    isLoading: isWalletLoading || isLoadingIssueState,
    userHasCard,
  }
}
